import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel, Tooltip, TextField, CircularProgress } from '@mui/material';
import { InfoOutlinedIcon, InsertPhotoOutlinedIcon } from 'icons/materialIcons';
import EmailSignatureProfilePicture from 'components/emailSignature/EmailSignatureProfilePicture';
import EmailSignatureCompanyLogo from 'components/emailSignature/EmailSignatureCompanyLogo';
import { useEmailSignatureContext, useInitializeEmailSignature } from 'contexts/EmailSignatureContext';
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi';
import EmailSignatureBannerimage from 'components/emailSignature/EmailSignatureBannerimage';
import EmailSignatureQrCodeLogo from 'components/emailSignature/EmailSignatureQrCodeLogo';

const EmailSignatureContainer = () => {
  const { cardId } = useParams();
  const [loading, setLoading] = useState(true);
  const [cardDetails, setCardDetails] = useState({});

  useInitializeEmailSignature(cardId);

  const {
    emailSignatureState,
    setEmailSignatureName,
    setEmailSignatureCompany,
    setEmailSignatureJobTitle,
    setEmailSignaturePhoneNumber,
    setEmailSignatureLocation,
    setEmailSignatureDisclaimer,
    setEmailSignatureBannerImageUrl,
    setEmailSignatureIncludeBannerImage,
    setEmailSignatureIncludeQRCode,
  } = useEmailSignatureContext();

  const {
    emailSignatureBannerImageUrl,
    emailSignatureName,
    emailSignatureJobTitle,
    emailSignatureProfilePictureUrl,
    emailSignatureCompany,
    emailSignaturePhoneNumber,
    emailSignatureLocation,
    emailSignatureDisclaimer,
    emailSignatureIncludeBannerImage,
    emailSignatureIncludeQRCode
  } = emailSignatureState;

  const [includeBanner, setIncludeBanner] = useState(emailSignatureState[cardId]?.emailSignatureIncludeBannerImage || false);

  const [includeQRCode, setIncludeQRCode] = useState(emailSignatureState[cardId]?.emailSignatureIncludeQRCode || true); // New state for QR code

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        const data = await getCardById(cardId);
        setCardDetails(data);
      } catch (error) {
        console.error('Error fetching card details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (cardId) {
      fetchCardDetails();
    }
  }, [cardId]);



  useEffect(() => {
    if (cardDetails?.card) {
      const { fullName, jobTitle, company, location, phoneNumber } = cardDetails.card.contact_info || {};

      // Update context state only if values have changed and are not undefined
      if (fullName && emailSignatureState[cardId]?.emailSignatureName !== fullName) setEmailSignatureName(cardId, fullName);
      if (jobTitle && emailSignatureState[cardId]?.emailSignatureJobTitle !== jobTitle) setEmailSignatureJobTitle(cardId, jobTitle);
      if (company && emailSignatureState[cardId]?.emailSignatureCompany !== company) setEmailSignatureCompany(cardId, company);
      if (location && emailSignatureState[cardId]?.emailSignatureLocation !== location) setEmailSignatureLocation(cardId, location);
      if (phoneNumber && emailSignatureState[cardId]?.emailSignaturePhoneNumber !== phoneNumber) setEmailSignaturePhoneNumber(cardId, phoneNumber);
    }
  }, [cardDetails, cardId]);




  const fileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setEmailSignatureBannerImageUrl(cardId, url);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setEmailSignatureBannerImageUrl(cardId, url);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleIncludeBannerChange = (event) => {
    const { checked } = event.target;
    setIncludeBanner(checked);
    setEmailSignatureIncludeBannerImage(cardId, checked);
  };


  const handleIncludeQRCodeChange = (event) => {
    const { checked } = event.target;
    setIncludeQRCode(checked);
    setEmailSignatureIncludeQRCode(cardId, checked);
  };




  useEffect(() => {
    console.log('Email Signature State:', emailSignatureState);
    if (emailSignatureState[cardId]) {
      console.log('Email Signature Details from the container for Card ID:', cardId);
      console.log('Profile Picture URL:', emailSignatureState[cardId]?.emailSignatureProfilePictureUrl); // Log Profile Picture URL
      // Other console logs
      console.log('Name:', emailSignatureState[cardId]?.emailSignatureName);
      console.log('Company:', emailSignatureState[cardId]?.emailSignatureCompany);
      console.log('Job Title:', emailSignatureState[cardId]?.emailSignatureJobTitle);
      console.log('Phone Number:', emailSignatureState[cardId]?.emailSignaturePhoneNumber);
      console.log('Location:', emailSignatureState[cardId]?.emailSignatureLocation);
      console.log('Disclaimer:', emailSignatureState[cardId]?.emailSignatureDisclaimer);
      console.log('Banner Image URL:', emailSignatureState[cardId]?.emailSignatureBannerImageUrl);
      console.log('Include Banner:', emailSignatureState[cardId]?.emailSignatureIncludeBannerImage);
      console.log('Include QR Code:', emailSignatureState[cardId]?.emailSignatureIncludeQRCode);
    }
  }, [emailSignatureState, cardId]);





  return (
    <Box my={4}>
      <Typography variant="body2"
        sx={{
          fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px', paddingLeft: 2
        }}
        gutterBottom align="left">
        Email Signature
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: 2,
            maxWidth: 800,
            margin: 'auto',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <EmailSignatureProfilePicture />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <EmailSignatureCompanyLogo />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <EmailSignatureQrCodeLogo />
            </Grid>
            <Grid item xs={12} sm={4} md={3}></Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeBanner}
                  onChange={handleIncludeBannerChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                  Include Banner
                </Typography>
              }
            />
            <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.6:1)" arrow>
              <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#4f4f4f' }} />
            </Tooltip>
          </Box>

          <Box>
            <EmailSignatureBannerimage />
          </Box>

          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f" }}>
                Name</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignatureName}
                onChange={(e) => setEmailSignatureName(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
              />
              {console.log(`From return  + ${emailSignatureState[cardId]?.emailSignatureName}`)}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                Job Title</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignatureJobTitle}
                onChange={(e) => setEmailSignatureJobTitle(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                Company</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignatureCompany}
                onChange={(e) => setEmailSignatureCompany(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                Phone Number</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignaturePhoneNumber}
                onChange={(e) => setEmailSignaturePhoneNumber(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                Location</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignatureLocation}
                onChange={(e) => setEmailSignatureLocation(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginTop: '10px' }}>
                Disclaimer</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                fullWidth
                value={emailSignatureState[cardId]?.emailSignatureDisclaimer}
                onChange={(e) => setEmailSignatureDisclaimer(cardId, e.target.value)}
                sx={{
                  backgroundColor: '#f7f7f7',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none', // Remove the border
                    },
                    '&:hover fieldset': {
                      border: 'none', // Remove the border on hover
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none', // Remove the border when focused
                    },
                  },
                }}
                multiline // Enables multiline input
                rows={2} // Number of visible rows (adjust as needed)
              />
            </Grid>


          </Grid>

          {/* Include QR Code Checkbox */}
          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeQRCode}
                  onChange={handleIncludeQRCodeChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#4f4f4f' }}>
                  Include QRCode
                </Typography>
              }
            />
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default EmailSignatureContainer;
